import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, MessageSquareText, ArrowRight, Check, X } from 'lucide-react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { whatsappAPI } from '../whatsappAPI';
import { useOrganizacao, Chip } from '../organizacao';

const VAZIA = { keyword: '', resposta: '', acao: 'responder', setor_id: '', ativo: true };
const errMsg = (e, p) => e?.response?.data?.detail || p;

export default function RegrasConfig({ admin }) {
  const { setores } = useOrganizacao();
  const [regras, setRegras] = useState([]);
  const [edit, setEdit] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const load = () => whatsappAPI.chatbotRegras().then(r => setRegras(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const setor = (id) => setores.find(s => s.id === id);

  const salvar = async (r = edit) => {
    setSalvando(true);
    try {
      const body = { ...r, setor_id: r.acao === 'setor' ? r.setor_id : null };
      if (r.id) await whatsappAPI.chatbotUpdate(r.id, body); else await whatsappAPI.chatbotCreate(body);
      if (r === edit) { toast.success(r.id ? 'Regra atualizada' : 'Regra criada'); setEdit(null); }
      load();
    } catch (e) { toast.error(errMsg(e, 'Não foi possível salvar')); }
    finally { setSalvando(false); }
  };
  const excluir = async (r) => {
    if (!window.confirm('Excluir esta regra?')) return;
    await whatsappAPI.chatbotDelete(r.id); load();
  };

  return (
    <Card className="p-5" data-testid="wa-config-regras">
      <div className="flex items-center justify-between gap-2 mb-1">
        <h3 className="font-semibold flex items-center gap-2"><MessageSquareText className="w-5 h-5 text-emerald-600" /> Respostas e encaminhamentos por palavra-chave</h3>
        {admin && !edit && <Button size="sm" onClick={() => setEdit({ ...VAZIA })} data-testid="wa-regra-nova"><Plus className="w-4 h-4 mr-1" /> Nova regra</Button>}
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        Enquanto o cliente está com o assistente, se a mensagem tiver uma das palavras (sem diferenciar acentos ou maiúsculas),
        o assistente <b>responde</b> com o texto e mostra o menu, ou <b>encaminha</b> direto para a fila de um setor.
        Ex.: “boleto, pagamento, pix” → Financeiro.
      </p>

      {edit && (
        <div className="rounded-xl border-2 border-emerald-200 dark:border-emerald-900 bg-emerald-50/40 dark:bg-emerald-950/20 p-4 mb-4 space-y-3" data-testid="wa-regra-form">
          <label className="block text-sm font-medium">Palavras-chave <span className="font-normal text-xs text-muted-foreground">(separe por vírgula)</span>
            <Input value={edit.keyword} onChange={e => setEdit({ ...edit, keyword: e.target.value })} placeholder="preço, valor, orçamento" className="mt-1" autoFocus data-testid="wa-bot-kw" />
          </label>
          <div className="flex gap-2 flex-wrap">
            {[['responder', 'Responder automaticamente'], ['setor', 'Encaminhar para um setor']].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setEdit({ ...edit, acao: k })} data-testid={`wa-regra-acao-${k}`}
                className={`h-8 px-3 rounded-full text-sm border ${edit.acao === k ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white dark:bg-slate-900'}`}>{l}</button>
            ))}
          </div>
          {edit.acao === 'setor' && (
            <div>
              {setores.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {setores.filter(s => s.ativo !== false).map(s => (
                    <button key={s.id} type="button" onClick={() => setEdit({ ...edit, setor_id: s.id })} data-testid={`wa-regra-setor-${s.id}`}
                      className={`h-8 px-3 rounded-full text-sm border flex items-center gap-1.5 ${edit.setor_id === s.id ? 'text-white' : 'bg-white dark:bg-slate-900'}`}
                      style={edit.setor_id === s.id ? { background: s.cor, borderColor: s.cor } : {}}>
                      <span className="w-2 h-2 rounded-full" style={{ background: edit.setor_id === s.id ? '#fff' : s.cor }} />{s.nome}
                    </button>
                  ))}
                </div>
              ) : <p className="text-sm text-amber-700">Cadastre os setores primeiro (aba Setores).</p>}
            </div>
          )}
          <label className="block text-sm font-medium">
            {edit.acao === 'setor' ? <>Mensagem antes de encaminhar <span className="font-normal text-xs text-muted-foreground">(opcional)</span></> : 'Resposta automática'}
            <textarea rows={3} value={edit.resposta || ''} onChange={e => setEdit({ ...edit, resposta: e.target.value })} data-testid="wa-bot-resp"
              placeholder={edit.acao === 'setor' ? 'Ex.: Certo! Vou te passar para o nosso financeiro.' : 'Ex.: Nosso endereço é…'}
              className="mt-1 w-full border rounded-md p-2 text-sm bg-background font-normal" />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => setEdit(null)}><X className="w-4 h-4 mr-1" />Cancelar</Button>
            <Button size="sm" onClick={() => salvar()} disabled={salvando} className="bg-emerald-600 hover:bg-emerald-700" data-testid="wa-bot-save">
              {salvando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Salvar
            </Button>
          </div>
        </div>
      )}

      <div className="grid gap-2">
        {regras.map(r => {
          const s = setor(r.setor_id);
          return (
            <div key={r.id} className={`p-3 rounded-xl border flex items-start gap-3 ${r.ativo === false ? 'opacity-55' : ''}`} data-testid={`wa-regra-${r.id}`}>
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="flex flex-wrap items-center gap-1">
                  {(r.keyword || '').split(',').map(k => k.trim()).filter(Boolean).map(k => (
                    <span key={k} className="text-xs font-medium px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800">{k}</span>
                  ))}
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400 mx-1" />
                  {r.acao === 'setor'
                    ? (s ? <Chip cor={s.cor}>{s.nome}</Chip> : <span className="text-xs text-red-600">setor excluído</span>)
                    : <span className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">responde</span>}
                </div>
                {r.resposta && <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-3">{r.resposta}</p>}
              </div>
              {admin && (
                <div className="flex items-center gap-1 shrink-0">
                  <label className="flex items-center gap-1 text-xs mr-1 cursor-pointer" title="Ativa">
                    <input type="checkbox" checked={r.ativo !== false} onChange={e => salvar({ ...r, ativo: e.target.checked })} className="w-4 h-4 accent-emerald-600" />
                  </label>
                  <Button size="sm" variant="ghost" onClick={() => setEdit({ ...VAZIA, ...r, acao: r.acao || 'responder', setor_id: r.setor_id || '' })}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => excluir(r)}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </div>
              )}
            </div>
          );
        })}
        {!regras.length && <p className="text-center text-sm text-muted-foreground p-4">Nenhuma regra cadastrada</p>}
      </div>
    </Card>
  );
}
